import type { ThemeOptions } from '@mui/material/styles';
import {
  ArrowDropDownIcon,
  CancelIcon,
  CheckCircleOutlineIcon,
  CloseIcon,
  ErrorOutlineIcon,
  InfoOutlinedIcon,
  KeyboardArrowDownIcon,
  WarningAmberIcon,
} from './index';

type Components = NonNullable<ThemeOptions['components']>;

/**
 * MUI components that render their own built-in Material glyphs (Alert
 * severity icons, Chip delete, Select/Autocomplete arrows). Swapping them via
 * `defaultProps` keeps the icon set consistent without touching call sites.
 */
export function getLucideMuiDefaultProps(): Components {
  return {
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          success: <CheckCircleOutlineIcon fontSize="inherit" />,
          info: <InfoOutlinedIcon fontSize="inherit" />,
          warning: <WarningAmberIcon fontSize="inherit" />,
          error: <ErrorOutlineIcon fontSize="inherit" />,
        },
      },
    },
    MuiChip: { defaultProps: { deleteIcon: <CancelIcon /> } },
    MuiSelect: { defaultProps: { IconComponent: KeyboardArrowDownIcon } },
    MuiAutocomplete: {
      defaultProps: {
        popupIcon: <ArrowDropDownIcon />,
        clearIcon: <CloseIcon fontSize="small" />,
      },
    },
  };
}

/**
 * Merges the Lucide `defaultProps` into an existing `components` map so that
 * `styleOverrides` already defined for the same component are preserved.
 */
export function withLucideMuiDefaults(components: Components = {}): Components {
  const d = getLucideMuiDefaultProps();
  const c = components;

  return {
    ...c,
    MuiAlert: {
      ...c.MuiAlert,
      defaultProps: { ...c.MuiAlert?.defaultProps, ...d.MuiAlert?.defaultProps },
    },
    MuiChip: {
      ...c.MuiChip,
      defaultProps: { ...c.MuiChip?.defaultProps, ...d.MuiChip?.defaultProps },
    },
    MuiSelect: {
      ...c.MuiSelect,
      defaultProps: { ...c.MuiSelect?.defaultProps, ...d.MuiSelect?.defaultProps },
    },
    MuiAutocomplete: {
      ...c.MuiAutocomplete,
      defaultProps: {
        ...c.MuiAutocomplete?.defaultProps,
        ...d.MuiAutocomplete?.defaultProps,
      },
    },
  };
}
